import { Slot } from "radix-ui";
// eslint-disable-next-line import/no-namespace -- shadcn vendor file
import * as React from "react";
import {
  Controller,
  type ControllerProps,
  type FieldPath,
  type FieldValues,
  FormProvider,
  type FormProviderProps,
  useFormContext,
  useFormState,
} from "react-hook-form";

import { cn } from "../../lib/utils";
import { Label } from "./label";

// ---------------------------------------------------------------------------
// FormField — thin Controller wrapper that injects field context
// ---------------------------------------------------------------------------
type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  name: TName;
};

const FormFieldContext = React.createContext<FormFieldContextValue>({} as FormFieldContextValue);

function FormField<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({ ...properties }: ControllerProps<TFieldValues, TName>) {
  return (
    <FormFieldContext.Provider value={{ name: properties.name }}>
      <Controller {...properties} />
    </FormFieldContext.Provider>
  );
}

function useFormField() {
  const fieldContext = React.useContext(FormFieldContext);
  const itemContext = React.useContext(FormItemContext);
  const { getFieldState } = useFormContext();
  const formState = useFormState({ name: fieldContext.name });
  const fieldState = getFieldState(fieldContext.name, formState);

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>");
  }

  const { id } = itemContext;

  return {
    id,
    name: fieldContext.name,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  };
}

// ---------------------------------------------------------------------------
// FormItem — wrapper div that provides a stable id via context
// ---------------------------------------------------------------------------
type FormItemContextValue = {
  id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

function FormItem({ className, ...properties }: React.ComponentProps<"div">) {
  const id = React.useId();
  return (
    <FormItemContext.Provider value={{ id }}>
      <div data-slot="form-item" className={cn("grid gap-2", className)} {...properties} />
    </FormItemContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// FormLabel — Label wired to the field id; turns destructive on error
// ---------------------------------------------------------------------------
function FormLabel({ className, ...properties }: React.ComponentProps<typeof Label>) {
  const { error, formItemId } = useFormField();
  return (
    <Label
      data-slot="form-label"
      className={cn("data-[error=true]:text-destructive", className)}
      data-error={!!error}
      htmlFor={formItemId}
      {...properties}
    />
  );
}

// ---------------------------------------------------------------------------
// FormControl — passes id and aria attributes to its single child via Slot.
// The child input receives `id={formItemId}` so FormLabel's htmlFor wires up.
// ---------------------------------------------------------------------------
function FormControl({ children, ...properties }: React.ComponentProps<typeof Slot.Root>) {
  const { error, formItemId, formDescriptionId, formMessageId } = useFormField();
  return (
    <Slot.Root
      data-slot="form-control"
      id={formItemId}
      aria-describedby={error ? `${formDescriptionId} ${formMessageId}` : formDescriptionId}
      aria-invalid={error ? true : undefined}
      {...properties}
    >
      {children}
    </Slot.Root>
  );
}

// ---------------------------------------------------------------------------
// FormDescription — optional helper text below the field
// ---------------------------------------------------------------------------
function FormDescription({ className, ...properties }: React.ComponentProps<"p">) {
  const { formDescriptionId } = useFormField();
  return (
    <p
      data-slot="form-description"
      id={formDescriptionId}
      className={cn("text-muted-foreground text-sm", className)}
      {...properties}
    />
  );
}

// ---------------------------------------------------------------------------
// FormMessage — renders the first Zod/RHF error for the field
// ---------------------------------------------------------------------------
function FormMessage({ className, ...properties }: React.ComponentProps<"p">) {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message ?? "") : properties.children;

  if (!body) {
    return null;
  }

  return (
    <p
      data-slot="form-message"
      id={formMessageId}
      className={cn("text-destructive -mt-1 text-2xs", className)}
      {...properties}
    >
      {body}
    </p>
  );
}

// Re-export FormProvider so consumers only need to import from this file
function Form<TFieldValues extends FieldValues, TContext = unknown>(
  properties: FormProviderProps<TFieldValues, TContext>
) {
  return <FormProvider {...properties} />;
}

export { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage, useFormField };
